import { Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import { formatDateTime, logoUrl } from "@/frontend/utils/shared";
import { usePatientConnectionsQuery } from "@/frontend/queries/dashboard/patients";
import { isConnectionHealthy } from "@/frontend/utils/dashboard/connections";
import {
  CONNECTION_CHANNEL_LABEL,
  CONNECTION_CHANNEL_TONE,
  CONNECTION_STATUS_LABEL,
} from "@/frontend/types/dashboard/connections";
import { EHR_PROVIDER_LABEL } from "@/frontend/constants/ehr-providers";

/**
 * Where this patient's records come from, one row per connected health system
 * with its channel, last pull, record count and share, and whether it can still
 * return records. It renders nothing until a source is connected, since the
 * onboarding panel above already says what to do in that state.
 */
export function RecordSourcesCard({ patientId }: { patientId: string }) {
  const { data: connectionList } = usePatientConnectionsQuery(patientId);

  const connections = connectionList?.connections ?? [];
  const recordTotal = connectionList?.recordTotal ?? 0;

  if (connections.length === 0) return null;

  return (
    <section className="flex max-h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border/60 bg-card">
      <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-border/60 px-4">
        <h2 className="text-meta font-semibold uppercase tracking-wide text-foreground">
          Where records come from
        </h2>
        <span className="text-meta tabular-nums text-muted-foreground">
          {recordTotal.toLocaleString()} total
        </span>
      </div>

      <div className="min-h-0 flex-1 divide-y divide-border/60 overflow-y-auto">
        {connections.map((connection) => {
          const logo = logoUrl(connection.sourceLogo);
          const share =
            recordTotal > 0 ? Math.round((connection.recordCount / recordTotal) * 100) : null;
          const healthy = isConnectionHealthy(connection.status);
          const provenance = [
            connection.sourceType
              ? (EHR_PROVIDER_LABEL[connection.sourceType] ?? connection.sourceType)
              : null,
            connection.lastPullAt
              ? `last pull ${formatDateTime(connection.lastPullAt)}`
              : "no pull yet",
          ].filter(Boolean);

          return (
            <Link
              key={connection.id}
              to="/dashboard/patients/$patientId/connections/$connectionId"
              params={{ patientId, connectionId: connection.id }}
              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/40"
            >
              <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background">
                {logo ? (
                  <img
                    src={logo}
                    alt=""
                    className="size-4 object-contain"
                    data-ph-no-capture
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <Building2 className="size-3.5 text-muted-foreground" />
                )}
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="line-clamp-2 break-words text-caption font-medium" data-patient>
                    {connection.sourceName}
                  </span>
                  <span
                    className={cn(
                      "shrink-0 rounded px-1.5 py-0.5 text-meta font-medium",
                      CONNECTION_CHANNEL_TONE[connection.channel],
                    )}
                  >
                    {CONNECTION_CHANNEL_LABEL[connection.channel]}
                  </span>
                </span>
                <span className="mt-0.5 block truncate text-meta text-muted-foreground">
                  {provenance.join(" · ")}
                </span>
              </span>

              <span className="shrink-0 text-right">
                <span className="block text-caption font-medium tabular-nums">
                  {connection.recordCount.toLocaleString()}
                </span>
                {share !== null && (
                  <span className="block text-meta tabular-nums text-muted-foreground">
                    {share}% of records
                  </span>
                )}
              </span>

              <span
                className={cn(
                  "shrink-0 rounded-full border px-2 py-0.5 text-meta font-medium",
                  healthy
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
                )}
              >
                {CONNECTION_STATUS_LABEL[connection.status] ?? connection.status}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
